import type { ClientAgentReference } from '@/agent/references/store/schemas'
import type { AgentReferenceDisplayGroup, FootnoteInfoByCitationId } from '@/agent/references/util/agent-reference-footnotes'
import AgentResponseReferencesPrintableListItem from './AgentResponseReferencesPrintableListItem'
import { extractCitationIds } from '@/util/agent-citations'
import { buildAgentReferenceDisplayGroups, getCitationIdsOutsideCitedReferenceGroups } from '@/agent/references/util/agent-reference-footnotes'
import type { AgentReferencesRecord } from '@/agent/references/store/slice'

type AgentResponseReferencesPrintableSectionProps = {
  responseMessage: string
  footnoteInfoByCitationId: FootnoteInfoByCitationId
  references: ClientAgentReference[]
}

/**
 * Agent Response References Printable Section
 *
 * A printable version of the AgentResponseReferencesSection/AgentResponseReferencesGroup component.
 * This is used to display the references section in the printable version of the agent response event bubble.
 */
export default function AgentResponseReferencesPrintableSection(props: AgentResponseReferencesPrintableSectionProps) {
  const { responseMessage, footnoteInfoByCitationId, references } = props

  // Identify the citation_ids in the responseMessage
  const matchedCitationIds = extractCitationIds(responseMessage)
  if (matchedCitationIds.length === 0) {
    return null
  }

  const turnCitationIds = Object.keys(footnoteInfoByCitationId)
  const usedCitationIds = matchedCitationIds.filter((citation_id) => turnCitationIds.includes(citation_id))

  const referencesByCitationId = references.reduce<AgentReferencesRecord>((record, reference) => {
    record[reference.reference.citation_id] = reference
    return record
  }, {})
  const unusedCitationIds = getCitationIdsOutsideCitedReferenceGroups(turnCitationIds, usedCitationIds, referencesByCitationId)

  const usedReferenceGroups = buildAgentReferenceDisplayGroups(usedCitationIds, footnoteInfoByCitationId, referencesByCitationId)
  const unusedReferenceGroups = buildAgentReferenceDisplayGroups(unusedCitationIds, footnoteInfoByCitationId, referencesByCitationId)
  const totalReferenceGroups = usedReferenceGroups.length + unusedReferenceGroups.length

  const renderReferenceItems = (referenceGroups: AgentReferenceDisplayGroup[]) =>
    referenceGroups
      .map((referenceGroup) => {
        const reference = referencesByCitationId[referenceGroup.displayCitationId]
        if (!reference) return null
        return (
          <AgentResponseReferencesPrintableListItem
            key={referenceGroup.displayCitationId}
            citationLabel={referenceGroup.footnoteNumber}
            reference={reference}
          />
        )
      })
      .filter((item): item is JSX.Element => item !== null)

  return (
    <div>
      {totalReferenceGroups > 0 && (
        <div>
          <hr />
          <h3>Cited References</h3>
          {renderReferenceItems(usedReferenceGroups)}
          {unusedReferenceGroups.length > 0 && (
            <>
              <hr />
              <h3>Related References</h3>
              {renderReferenceItems(unusedReferenceGroups)}
            </>
          )}
          <hr />
        </div>
      )}
    </div>
  )
}
